import { useEffect, useState } from 'react';

import {
  fetchMovedTarget,
  forwardUrl,
  leaveFor,
  readMovedTarget,
  rememberMovedTarget,
} from '../../core/moved.js';
import { useAppStore } from '../../state/store.js';
import { SyncStatus } from '../SyncStatus.js';
import { browserOf } from '../onboarding/tips.js';

import type { SyncEngine } from '../../core/sync/engine.js';
import type { BrowserInfo } from '../onboarding/tips.js';

/**
 * Экран переезда: «Nedelnik теперь Onlap» (этап 27 плана, «PWA и локальная
 * копия»).
 *
 * Показывается вместо приложения на старом адресе. Всё, что лежит у
 * приложения на устройстве, — копия задач, очередь неотправленных правок,
 * Service Worker, значок — привязано к старому домену и на новый не переедет.
 * Поэтому экран не пускает дальше, пока очередь не опустела: иначе
 * неотправленное осталось бы в старой копии навсегда.
 */

/**
 * Куда переехали: сначала по памяти устройства, потом по ответу сервера.
 *
 * Сервер спрашивается и при возвращении к приложению: установленное на
 * телефоне неделями не перезапускается, а переключатель на сервере могут
 * включить или откатить в любой момент.
 */
export function useMovedTarget(): string | undefined {
  const [target, setTarget] = useState(() => readMovedTarget());

  useEffect(() => {
    let live = true;

    function ask(): void {
      if (globalThis.document?.visibilityState === 'hidden') return;
      fetchMovedTarget().then(
        (value) => {
          rememberMovedTarget(value);
          if (live) setTarget(value);
        },
        // Не дозвонились — остаётся то, что помнили.
        () => undefined,
      );
    }

    ask();
    globalThis.document?.addEventListener('visibilitychange', ask);
    globalThis.addEventListener?.('online', ask);
    return () => {
      live = false;
      globalThis.document?.removeEventListener('visibilitychange', ask);
      globalThis.removeEventListener?.('online', ask);
    };
  }, []);

  return target;
}

/**
 * Согласие для коннектора на старом адресе — сразу на новый, с той же строкой
 * запроса.
 *
 * Для браузера без Service Worker это делает редирект Caddy, а с ним переход
 * на `/authorize` перехватывает Service Worker и отдаёт оболочку. Экран
 * переезда здесь был бы тупиком: коннектор ждёт согласия, а не инструкцию.
 * Код потом примет любой адрес — он лежит в базе (этап 27, «OAuth через
 * старый адрес»). Очередь правок согласию не мешает: копия остаётся на месте.
 */
export function ForwardToMoved({ target }: { target: string }): React.ReactElement {
  const url = forwardUrl(target, globalThis.location);

  useEffect(() => {
    globalThis.location.replace(url);
  }, [url]);

  return (
    <main className="boot">
      Переходим на <a href={url}>{new URL(target).host}</a>…
    </main>
  );
}

export function MovedScreen({
  target,
  engine,
  signedIn,
  onSignIn,
  leave = leaveFor,
  userAgent = globalThis.navigator?.userAgent ?? '',
}: {
  target: string;
  engine: Pick<SyncEngine, 'pending' | 'flush'>;
  /** Есть ли вход на этом адресе: без него очередь не уедет. */
  signedIn: boolean;
  /** Войти здесь же — ради того, чтобы досылать очередь. */
  onSignIn: () => void;
  leave?: (target: string) => Promise<boolean>;
  userAgent?: string;
}): React.ReactElement {
  const status = useAppStore((state) => state.status);
  const [pending, setPending] = useState<number | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [leaving, setLeaving] = useState(false);
  const host = new URL(target).host;

  // Очередь перечитывается на каждую смену состояния синхронизации: правка
  // уехала — движок говорит «сохранено», и кнопка оживает без перезапуска.
  useEffect(() => {
    let live = true;
    void engine.pending().then(
      (count) => {
        if (live) setPending(count);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [engine, status]);

  // Досылаем сразу, а не после паузы: человек стоит перед кнопкой и ждёт её.
  useEffect(() => {
    if (signedIn) void engine.flush();
  }, [engine, signedIn]);

  const ready = pending === 0 && !leaving;

  function go(): void {
    if (pending === undefined) return;
    if (pending > 0) {
      setNotice(
        signedIn
          ? 'Сначала правки должны уехать на сервер — для этого нужна сеть.'
          : 'Сначала войдите здесь, чтобы правки уехали на сервер.',
      );
      return;
    }

    setLeaving(true);
    setNotice(undefined);
    void leave(target).then(
      (left) => {
        if (left) return;
        setLeaving(false);
        setNotice(`${host} сейчас не отвечает. Проверьте сеть и нажмите ещё раз.`);
      },
      () => setLeaving(false),
    );
  }

  return (
    <main className="login moved">
      <section className="login__intro">
        <p className="login__brand">
          <img src="/icon.svg" alt="" width="28" height="28" />
          Onlap
        </p>
        <h1 className="login__title">Nedelnik теперь Onlap</h1>
        <p className="login__lede">
          Тот же трекер под новым именем и по новому адресу — {host}. Задачи хранятся на сервере: на
          новом адресе войдите, и они будут на месте.
        </p>
      </section>

      <section className="login__card" aria-label="Переезд">
        <QueueState pending={pending} signedIn={signedIn} onSignIn={onSignIn} />

        <button
          type="button"
          className="login__primary"
          aria-disabled={!ready}
          aria-describedby="moved-queue"
          onClick={go}
        >
          {leaving ? `Открываем ${host}…` : `Открыть ${host}`}
        </button>

        {notice !== undefined && (
          <p className="login__error" role="alert">
            {notice}
          </p>
        )}

        <InstallSteps info={browserOf(userAgent)} host={host} />
      </section>
    </main>
  );
}

/** Что с очередью: от этого зависит, можно ли уходить. */
function QueueState({
  pending,
  signedIn,
  onSignIn,
}: {
  pending: number | undefined;
  signedIn: boolean;
  onSignIn: () => void;
}): React.ReactElement {
  if (pending === undefined) {
    return (
      <p id="moved-queue" className="login__hint">
        Проверяем, всё ли отправлено…
      </p>
    );
  }

  if (pending === 0) {
    return (
      <p id="moved-queue" className="login__hint">
        Все правки на сервере — можно переходить.
      </p>
    );
  }

  if (!signedIn) {
    return (
      <div className="moved__queue">
        <p id="moved-queue" className="login__hint">
          Правок не на сервере: {pending}, а вход здесь закончился. Войдите, чтобы они уехали, —
          иначе останутся только на этом устройстве.
        </p>
        <button type="button" onClick={onSignIn}>
          Войти здесь
        </button>
      </div>
    );
  }

  return (
    <div className="moved__queue">
      <p id="moved-queue" className="login__hint">
        Правок ещё не на сервере: {pending}. Кнопка заработает, как только всё уедет.
      </p>
      <SyncStatus label="Отправка правок" />
    </div>
  );
}

/**
 * Как поставить приложение заново и убрать старое.
 *
 * Значок на экране «Домой» привязан к старому адресу, и переставить его на
 * новый нельзя — только завести новый. Своего телефона показываются только
 * его шаги; на компьютере — оба набора: его спрашивают и для телефона.
 */
function InstallSteps({ info, host }: { info: BrowserInfo; host: string }): React.ReactElement {
  const sets = installSteps(info, host);

  return (
    <div className="moved__steps">
      <h2 className="login__heading">Приложение на телефоне</h2>
      {sets.map((set) => (
        <section key={set.title} aria-label={set.title}>
          {sets.length > 1 && <h3 className="moved__platform">{set.title}</h3>}
          <ol className="moved__list">
            {set.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

export interface StepSet {
  title: string;
  steps: readonly string[];
}

export function installSteps(info: BrowserInfo, host: string): StepSet[] {
  const login =
    'Откройте Onlap со значка и войдите кодом из письма. Ключ входа заведите заново в настройках: старый работает только на прежнем адресе.';

  const iphone: StepSet = {
    title: 'iPhone',
    steps: [
      `Откройте ${host} в Safari — кнопкой выше или набрав адрес.`,
      '«Поделиться» → «На экран Домой» → «Добавить».',
      login,
      'Старый значок Nedelnik удалите: удержите на нём палец и выберите «Удалить закладку».',
    ],
  };

  const android: StepSet = {
    title: 'Android',
    steps: [
      `Откройте ${host} в браузере — кнопкой выше или набрав адрес.`,
      'В меню браузера (три точки) выберите «Установить приложение» или «Добавить на главный экран».',
      login,
      'Старый значок Nedelnik удалите: удержите на нём палец и выберите «Удалить».',
    ],
  };

  if (info.platform === 'ios') return [iphone];
  if (info.platform === 'android') return [android];
  return [iphone, android];
}
